<template>
  <div class="wd-my-3">
    <div :class="{ 'wd-border-0 wd-border-b wd-border-solid wd-border-gray40': !isEditing }">
      <div class="wd-flex wd-items-center wd-justify-between wd-py-3">
        <div class="wd-flex wd-items-center wd-gap-2">
          <span class="wd-text-sm wd-text-black50">{{ fieldTitle }}</span>
          <span v-if="showTodoBadge" class="wd-text-otempmin wd-text-red70 wd-bg-red10 wd-rounded wd-px-2 wd-py-0.5">
            {{ $t('member_profile_need_complete_hint') }}
          </span>
        </div>

        <button
          v-if="isEditable"
          class="wd-text-base wd-text-orange50 disabled:wd-text-gray60 wd-pl-3 wd-py-1"
          :disabled="isCtaDisabled"
          @click="onCtaClick"
        >
          {{ ctaText }}
        </button>
      </div>

      <div v-if="isEditing" class="wd-my-2 wd-flex wd-gap-2">
        <component
          :is="field.component"
          ref="fieldComponentRef"
          v-model="draft"
          :placeholder="fieldPlaceholder"
          v-bind="fieldExtraProps"
          @blur="validate"
        />
        <v-btn
          class="wd-text-lg"
          color="orange50"
          variant="flat"
          :width="68"
          :height="40"
          :disabled="isSaving || isDraftEmpty || !meta.valid"
          :loading="isSaving"
          @click="onSave"
        >
          {{ $t('all_save') }}
        </v-btn>
      </div>

      <p v-else class="!wd-mb-0 wd-text-base wd-text-black50 wd-py-4">
        {{ displayText }}
      </p>
    </div>

    <p v-if="field.note" class="!wd-mb-0 wd-py-2 wd-text-sm wd-text-gray80">{{ field.note }}</p>
  </div>
</template>

<script setup lang="ts">
import { object as yupObject } from 'yup'
import type { AnySchema } from 'yup'
import { useForm } from 'vee-validate'

import { useMemberProfileField } from '@/composables/useMemberProfileField'

import { Gender, MemberForm, genderI18nKeyMap } from '@/view-models/member'
import type { MemberFormItem, MemberFormValue } from '@/view-models/member'

interface Props {
  field: MemberFormItem
  schema: AnySchema
  value: MemberFormValue
}

const props = defineProps<Props>()

const editingKey = defineModel<string>('editingKey', { default: '' })

const { t } = useI18n()
const { saveField, isSaving } = useMemberProfileField()

const fieldComponentRef = useTemplateRef<{ confirmBeforeSave?: () => Promise<boolean> }>('fieldComponentRef')

// === Field Display State ===
const fieldTitle = computed(() => props.field.titleText)
const isEditing = computed(() => editingKey.value === props.field.key)
const isCtaDisabled = computed(() => !!(editingKey.value && !isEditing.value))
const isEditable = computed(() => props.field.isEditable !== false)
const ctaText = computed(() => (isEditing.value ? t('all_cancel') : t('profile_edit_field')))

const isEmpty = computed(() => {
  if (props.field.key === MemberForm.GENDER) {
    return props.value === null || props.value === Gender.UNSPECIFIED
  }
  return props.value === null || props.value === ''
})
const showTodoBadge = computed(() => !!props.field.isNeedComplete && isEmpty.value)

const displayText = computed(() => {
  if (isEmpty.value) return t('profile_not_provided')
  if (props.field.key === MemberForm.GENDER) {
    const key = genderI18nKeyMap[props.value as Gender]
    return key ? t(key) : t('profile_not_provided')
  }
  return `${props.value}`
})

const fieldPlaceholder = computed(() => (props.field.type === 'text' ? fieldTitle.value : t('profile_not_provided')))
const fieldExtraProps = computed(() => {
  if (props.field.type === 'select') return { ...props.field.componentProps }
  if (props.field.type === 'date') return { ...props.field.componentProps, errorMessage: errorMessage.value, validate }
  return { ...props.field.componentProps, errorMessage: errorMessage.value }
})

// === Form Setup ===
const initialValue = computed(() => {
  if (props.field.getDefaultValue) return props.field.getDefaultValue(props.value)
  return props.value ?? ''
})

const validationSchema = computed(() => yupObject({ [props.field.key]: props.schema }))

const { meta, errors, handleSubmit, defineField, resetForm, validateField } = useForm({
  validationSchema,
  initialValues: { [props.field.key]: initialValue.value },
})

const [draft] = defineField(props.field.key)

const errorMessage = computed(() => errors.value[props.field.key])
const isDraftEmpty = computed(() => draft.value === null || draft.value === undefined || draft.value === '')

watch(
  () => props.schema,
  () => {
    if (errorMessage.value) {
      validate()
    }
  }
)

// === Field Actions ===
function validate() {
  validateField(props.field.key)
}

function startEdit() {
  resetForm({ values: { [props.field.key]: initialValue.value } })
  editingKey.value = props.field.key
}

function cancelEdit() {
  editingKey.value = ''
}

function onCtaClick() {
  if (isEditing.value) {
    cancelEdit()
  } else {
    startEdit()
  }
}

// === Save Action ===
const onSave = handleSubmit(async () => {
  const confirmBeforeSave = fieldComponentRef.value?.confirmBeforeSave
  if (confirmBeforeSave && !(await confirmBeforeSave())) return

  const isSaved = await saveField(props.field.key, draft.value, fieldTitle.value)
  if (isSaved) editingKey.value = ''
})
</script>

<style lang="scss" scoped>
:deep(.v-field:hover .v-field__outline),
:deep(.v-field--focused .v-field__outline) {
  color: rgb(var(--v-theme-orange50)) !important;
}
</style>
